// TROIA-V3-FRONTEND/src/modules/crm/seller-dashboard/components/MyLeadsCard.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import type { LeadCaptureSession, LeadResponse } from '@troia-v3/types';

import { DashCard } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import { formatPhoneBR, formatRelative, type SellerPeriod, type SellerPeriodMode } from '../lib/sellerFormat';

import '../seller-dashboard.css';

interface MyLeadsCardProps {
  leads: LeadResponse[];
  /** Modo do seletor da página — no personalizado nenhum chip fica marcado. */
  period: SellerPeriodMode;
  /** Clicar num chip volta para aquele período pronto (sai do personalizado). */
  onPeriodChange: (p: SellerPeriod) => void;
  pending: LeadCaptureSession[];
}

const CHIPS: Array<{ id: SellerPeriod; label: string }> = [
  { id: 'today', label: 'Hoje' }, { id: 'week', label: 'Semana' }, { id: 'month', label: 'Mês' },
];

/** "Meus leads" — mockup 07 (.ld-row). Linha âmbar quando a sessão de captura do lead está aguardando WhatsApp (variante B). */
export const MyLeadsCard: React.FC<MyLeadsCardProps> = ({ leads, period, onPeriodChange, pending }) => {
  const waitingLeadIds = new Set(pending.filter((s) => s.status === 'filled' && s.leadId).map((s) => s.leadId as string));
  return (
    <DashCard icon="users" title="Meus leads" subtitle="Leads em que você é o responsável" testId="seller-my-leads" noPadding
      headerRight={
        <div className="flex gap-1.5 flex-wrap" role="group" aria-label="Período dos leads">
          {CHIPS.map((c) => (
            <button key={c.id} type="button" className="seller-chip" aria-pressed={period === c.id}
              onClick={() => onPeriodChange(c.id)} data-testid={`seller-my-leads-chip-${c.id}`}>{c.label}</button>
          ))}
        </div>
      }>
      <div className="py-1.5">
        {leads.length === 0 && (
          <div className="px-5 py-10 text-center text-[13px]" style={{ color: 'var(--theme-muted-text)' }} data-testid="seller-my-leads-empty">
            Nenhum lead seu neste período.
          </div>
        )}
        {leads.map((lead) => {
          const waiting = waitingLeadIds.has(lead.id);
          // `LeadResponse.contact.identifiers` (whatsapp[] / phone[]) — não existe `contact.phones` nem `lead.name` no types.
          const phone = lead.contact?.identifiers?.whatsapp?.[0] ?? lead.contact?.identifiers?.phone?.[0] ?? '';
          return (
            <div key={lead.id} data-testid={`seller-lead-row-${lead.id}`}
              className="grid grid-cols-[1fr_auto_auto] gap-3 items-center px-4 py-2.5 border-t first:border-t-0"
              style={{ borderColor: 'var(--theme-card-border)', ...(waiting ? { backgroundColor: 'var(--theme-warning-subtle)' } : {}) }}>
              <div className="min-w-0">
                <div className="font-semibold text-sm tracking-[-.01em] truncate" style={{ color: 'var(--theme-card-text)' }}>{lead.contact?.name ?? 'Lead'}</div>
                <div className="text-xs flex gap-2 items-center mt-px flex-wrap" style={{ color: 'var(--theme-muted-text)' }}>
                  <Icon name="phone" size={14} />{formatPhoneBR(phone)}
                  {waiting
                    ? <> · <span className="font-semibold" style={{ color: 'var(--theme-warning-fg)' }}>aguardando confirmação no WhatsApp</span></>
                    : lead.origin === 'qrcode' && <> · <span className="inline-flex items-center gap-1 rounded-full px-[7px] text-[11px] font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}><Icon name="qr-code" size={14} />QR</span></>}
                </div>
              </div>
              <span className="text-[11px] font-semibold rounded-full px-2 py-px whitespace-nowrap" style={{ color: 'var(--theme-card-text)', border: '1px solid var(--theme-input-border)' }}>{lead.step?.name ?? '—'}</span>
              <span className="text-xs whitespace-nowrap" style={{ color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }}>{formatRelative(String(lead.createdAt))}</span>
            </div>
          );
        })}
      </div>
      <div className="px-4 py-2.5" style={{ borderTop: '1px solid var(--theme-card-border)' }}>
        <Link to="/crm/leads" className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="seller-my-leads-all">
          Ver todos no CRM <Icon name="arrow-right" size={12} />
        </Link>
      </div>
    </DashCard>
  );
};
MyLeadsCard.displayName = 'MyLeadsCard';
